import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Save, Trash2, RotateCcw, Image as ImageIcon, Tag, Ruler, Sparkles, FileText, ExternalLink, Loader2 } from 'lucide-react';
import { HudButton, HudBadge } from '../hud';
import { getConfidenceTone, formatConfidence } from '../../data/materialthequeMockData';
import MaterialThumbnail from './MaterialThumbnail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FINISH_PRESETS = ['Mat', 'Mat huilé', 'Mat structuré', 'Brillant', 'Satiné', 'Brossé', 'Vieillie', 'Anti-dérapant R10', 'Boucle', 'Verni mat', 'Brut autoclave', 'Poli mat'];

/**
 * MaterialEditDrawer — tiroir latéral d'édition d'un matériau extrait par l'IA.
 * Inspiré de CrmContactDrawer (itération 40).
 *
 * Props:
 *  - material: { id, name, ref, category, finish, dimensions, confidence, source_page, thumb_color, thumb_pattern, thumb? }
 *  - onClose: () => void
 *  - onSave: (updated) => void
 *  - onResetToAI: (id) => void  — restaurer la valeur IA d'origine
 *  - onDelete: (id) => void
 */
const MaterialEditDrawer = ({ material, onClose, onSave, onResetToAI, onDelete, onGenerate, isGenerating }) => {
  const [draft, setDraft] = useState(material);
  const [dirty, setDirty] = useState(false);
  const [uploadingTexture, setUploadingTexture] = useState(false);
  const textureInputRef = useRef(null);
  // Sprint Feb-2026 : linked starred-image references for the 3D generator.
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);

  useEffect(() => { setDraft(material); setDirty(false); }, [material]);

  // Load starred image candidates for the same project.
  useEffect(() => {
    const mid = material?.id || material?.material_id;
    if (!mid) return;
    setCandidatesLoading(true);
    axios.get(`${API}/materialtheque/materials/${mid}/candidate-files`, { withCredentials: true })
      .then((r) => {
        setCandidates(r.data?.candidates || []);
        // Hydrate draft.linked_file_ids from server source of truth on open
        setDraft((d) => ({ ...d, linked_file_ids: r.data?.already_linked || [] }));
      })
      .catch(() => setCandidates([]))
      .finally(() => setCandidatesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [material?.id, material?.material_id]);

  if (!material) return null;

  const setField = (key, val) => {
    setDraft((d) => ({ ...d, [key]: val }));
    setDirty(true);
  };

  const handleTextureUpload = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 12 Mo)');
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type || '')) {
      toast.error('Format non supporté (PNG / JPG / WEBP uniquement)');
      return;
    }
    setUploadingTexture(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(
        `${API}/materialtheque/materials/${material.id || material.material_id}/texture`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      // Update local draft with the returned thumb_url (so the thumbnail re-renders)
      const updated = r.data || {};
      setDraft((d) => ({
        ...d,
        thumb: `${API}${updated.thumb_url || ''}?t=${Date.now()}`,
        thumb_url: updated.thumb_url,
      }));
      setDirty(true);
      toast.success('Texture mise à jour ✓');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'upload');
    } finally { setUploadingTexture(false); }
  };

  const tone = getConfidenceTone(draft.confidence);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1400] animate-in fade-in duration-200"
        onClick={onClose}
        data-testid="material-drawer-backdrop"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 w-full max-w-[480px] flex flex-col z-[1410] animate-in slide-in-from-right duration-300"
        style={{
          background: 'linear-gradient(to bottom, #0F0F12, #09090B)',
          borderLeft: '1px solid var(--c-border)',
          boxShadow: 'var(--shadow-cockpit)',
        }}
        data-testid="material-edit-drawer"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div
          className="flex items-start justify-between gap-3 px-5 py-4 shrink-0"
          style={{ borderBottom: '1px solid var(--c-border)' }}
        >
          <div className="min-w-0 flex-1">
            <span className="hud-eyebrow hud-eyebrow-gold">Édition matériau</span>
            <h3
              className="mt-1 font-semibold tracking-tight truncate"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
            >
              {draft.name || 'Matériau sans nom'}
            </h3>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <HudBadge tone={tone.tone}>
                <Sparkles className="w-3 h-3" />
                IA · {formatConfidence(draft.confidence)} · {tone.label}
              </HudBadge>
              {draft.ref && (
                <span className="text-[11px] hud-tabular" style={{ color: 'var(--c-text-muted)' }}>
                  {draft.ref}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="material-drawer-close"
            aria-label="Fermer"
            className="hud-btn hud-btn-ghost hud-btn-sm aspect-square px-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Aperçu texture */}
          <section>
            <span className="hud-eyebrow">Aperçu texture</span>
            <div className="mt-2 flex items-stretch gap-3">
              <MaterialThumbnail
                color={draft.thumb_color}
                pattern={draft.thumb_pattern}
                src={draft.thumb}
                size={96}
                rounded={16}
                label={draft.name}
              />
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                <input
                  ref={textureInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  data-testid="material-upload-texture-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleTextureUpload(f);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  className="hud-btn hud-btn-secondary hud-btn-sm w-full"
                  onClick={() => textureInputRef.current?.click()}
                  disabled={uploadingTexture}
                  data-testid="material-upload-texture"
                >
                  {uploadingTexture
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <ImageIcon className="w-3.5 h-3.5" />}
                  Remplacer la texture
                </button>
                <button
                  type="button"
                  className="hud-btn hud-btn-ghost hud-btn-sm w-full"
                  onClick={() => onResetToAI && onResetToAI(material.id)}
                  data-testid="material-reset-ai"
                  title="Restaurer la valeur extraite par l'IA"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Restaurer valeur IA
                </button>
              </div>
            </div>
          </section>

          {/* Identité */}
          <section className="space-y-3">
            <span className="hud-eyebrow">Identité</span>

            <Field label="Nom du matériau" icon={FileText}>
              <input
                value={draft.name || ''}
                onChange={(e) => setField('name', e.target.value)}
                data-testid="material-edit-name"
                className="hud-input w-full"
              />
            </Field>

            <Field label="Référence interne" icon={Tag}>
              <input
                value={draft.ref || ''}
                onChange={(e) => setField('ref', e.target.value)}
                data-testid="material-edit-ref"
                className="hud-input w-full hud-tabular"
                placeholder="ex. BAT-PARQ-MONT-RBL"
              />
            </Field>

            <Field label="Catégorie">
              <input
                value={draft.category || ''}
                onChange={(e) => setField('category', e.target.value)}
                data-testid="material-edit-category"
                className="hud-input w-full"
                placeholder="Sol, Mur, Crédence, Sanitaire…"
              />
            </Field>
          </section>

          {/* Spécifications */}
          <section className="space-y-3">
            <span className="hud-eyebrow">Spécifications</span>

            <Field label="Finition">
              <select
                value={draft.finish || ''}
                onChange={(e) => setField('finish', e.target.value)}
                data-testid="material-edit-finish"
                className="hud-input w-full"
              >
                <option value="">— Sélectionner —</option>
                {FINISH_PRESETS.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
                {draft.finish && !FINISH_PRESETS.includes(draft.finish) && (
                  <option value={draft.finish}>{draft.finish}</option>
                )}
              </select>
            </Field>

            <Field label="Dimensions" icon={Ruler}>
              <input
                value={draft.dimensions || ''}
                onChange={(e) => setField('dimensions', e.target.value)}
                data-testid="material-edit-dimensions"
                className="hud-input w-full"
                placeholder="ex. 600 × 1200 mm"
              />
            </Field>
          </section>

          {/* Source IA */}
          {draft.source_page && (
            <section
              className="rounded-xl px-4 py-3 flex items-center justify-between gap-3"
              style={{
                background: 'var(--c-surface-2)',
                border: '1px solid var(--c-border)',
              }}
            >
              <div className="min-w-0">
                <span className="hud-eyebrow">Source CCTP</span>
                <p className="text-[13px] mt-1" style={{ color: 'var(--c-text)' }}>
                  Page <span className="hud-tabular font-semibold">{draft.source_page}</span>
                </p>
              </div>
              <button
                className="hud-btn hud-btn-ghost hud-btn-sm"
                onClick={() => alert('Ouverture extrait PDF — à brancher')}
                data-testid="material-view-source"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Voir
              </button>
            </section>
          )}

          {/* ============================================================ */}
          {/* Références visuelles liées (Sprint Feb-2026 — C.a)          */}
          {/* ============================================================ */}
          <LinkedReferencesSection
            candidates={candidates}
            loading={candidatesLoading}
            selected={draft.linked_file_ids || []}
            onChange={(next) => {
              setDraft((d) => ({ ...d, linked_file_ids: next }));
              setDirty(true);
            }}
          />
        </div>

        {/* Footer */}
        <div
          className="px-5 py-4 flex items-center justify-between gap-3 shrink-0"
          style={{ borderTop: '1px solid var(--c-border)' }}
        >
          <button
            className="hud-btn hud-btn-danger hud-btn-sm"
            onClick={() => onDelete && onDelete(material.id)}
            data-testid="material-delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Supprimer
          </button>

          <div className="flex items-center gap-2">
            <HudButton variant="ghost" size="sm" onClick={onClose} data-testid="material-cancel">
              Annuler
            </HudButton>
            <HudButton
              variant="primary"
              size="sm"
              disabled={!dirty}
              onClick={() => { onSave && onSave(draft); }}
              leftIcon={<Save className="w-3.5 h-3.5" />}
              data-testid="material-save"
            >
              Enregistrer
            </HudButton>
            {onGenerate && (() => {
              const linkedCount = (draft.linked_file_ids || []).length;
              const canGen = linkedCount > 0 && !dirty && !isGenerating;
              return (
                <button
                  type="button"
                  onClick={canGen ? () => onGenerate(material.id || material.material_id, draft.name) : undefined}
                  disabled={!canGen}
                  data-testid="material-generate-drawer"
                  title={
                    linkedCount === 0
                      ? "Liez d'abord ≥1 référence visuelle"
                      : dirty
                        ? "Enregistrez d'abord vos modifications"
                        : isGenerating
                          ? "Génération en cours…"
                          : `Générer un rendu 3D focalisé (${linkedCount} référence${linkedCount > 1 ? 's' : ''} liée${linkedCount > 1 ? 's' : ''})`
                  }
                  className="hud-btn hud-btn-sm"
                  style={{
                    background: canGen ? 'linear-gradient(135deg, rgba(217,160,91,0.30), rgba(251,191,36,0.18))' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${canGen ? 'rgba(217,160,91,0.70)' : 'var(--c-border)'}`,
                    color: canGen ? '#FDE68A' : 'rgba(255,255,255,0.30)',
                    cursor: canGen ? 'pointer' : 'not-allowed',
                  }}
                >
                  <Sparkles className={`w-3.5 h-3.5 ${isGenerating ? 'animate-spin' : ''}`} />
                  {isGenerating ? 'Génération…' : 'Générer ce matériau'}
                </button>
              );
            })()}
          </div>
        </div>
      </aside>

      <style>{`
        .hud-input {
          background: var(--c-surface-2);
          border: 1px solid var(--c-border);
          color: var(--c-text);
          border-radius: 10px;
          padding: 8px 12px;
          font-size: 13.5px;
          font-family: inherit;
          transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
        }
        .hud-input::placeholder { color: var(--c-text-faint); }
        .hud-input:focus {
          outline: none;
          border-color: var(--c-border-accent);
          background: var(--c-surface-3);
          box-shadow: 0 0 0 3px rgba(217,160,91,0.12);
        }
      `}</style>
    </>
  );
};

const Field = ({ label, icon: Icon, children }) => (
  <label className="block">
    <span className="flex items-center gap-1.5 text-[11px] mb-1.5" style={{ color: 'var(--c-text-2)' }}>
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </span>
    {children}
  </label>
);

/**
 * Sprint Feb-2026 — C.a : références visuelles liées au matériau.
 *
 * Affiche la liste des fichiers IMAGE étoilés ⭐ du projet (candidats à
 * la génération 3D) sous forme de tuiles cochables. Le check/uncheck met
 * à jour `draft.linked_file_ids` du drawer parent — qui sera persisté par
 * PUT /materialtheque/materials/{id} au save.
 *
 * Si aucun candidat : message d'invitation à étoiler des références dans
 * l'onglet Fichiers.
 */
const LinkedReferencesSection = ({ candidates, loading, selected, onChange }) => {
  const selectedSet = new Set(selected || []);
  const toggle = (fileId) => {
    const next = new Set(selectedSet);
    if (next.has(fileId)) next.delete(fileId);
    else next.add(fileId);
    onChange(Array.from(next));
  };
  return (
    <section data-testid="material-linked-refs-section">
      <Field label="Références visuelles étoilées ⭐ liées" icon={ImageIcon}>
        {loading ? (
          <div className="flex items-center gap-2 text-[12px] py-3" style={{ color: 'var(--c-text-muted)' }}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement des références…
          </div>
        ) : candidates.length === 0 ? (
          <div
            className="rounded-lg py-4 px-3 text-center"
            style={{ background: 'var(--c-surface-2)', border: '1px dashed var(--c-border)' }}
            data-testid="material-linked-refs-empty"
          >
            <p className="text-[11.5px]" style={{ color: 'var(--c-text-2)' }}>
              Aucune référence étoilée ⭐ dans ce projet pour le moment.
            </p>
            <p className="text-[10.5px] mt-1" style={{ color: 'var(--c-text-faint)' }}>
              Ouvrez l'onglet <strong>Fichiers</strong> du projet et cliquez sur l'étoile
              dorée d'une image (porte, cuisine, etc.) pour qu'elle apparaisse ici.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2" data-testid="material-linked-refs-grid">
            {candidates.map((c) => {
              const isLinked = selectedSet.has(c.file_id);
              return (
                <button
                  key={c.file_id}
                  type="button"
                  onClick={() => toggle(c.file_id)}
                  data-testid={`material-linked-ref-${c.file_id}`}
                  title={`${c.filename}${c.subject ? ` — ${c.subject}` : ''}${isLinked ? ' (lié — cliquer pour délier)' : ' (cliquer pour lier)'}`}
                  className="group relative rounded-md overflow-hidden aspect-square transition-all"
                  style={{
                    border: isLinked
                      ? '2px solid #D9A05B'
                      : '1px solid var(--c-border)',
                    boxShadow: isLinked ? '0 0 0 2px rgba(217,160,91,0.18)' : 'none',
                    background: 'var(--c-surface-2)',
                  }}
                >
                  <img
                    src={`${API}/files/${c.file_id}/download`}
                    alt={c.filename}
                    crossOrigin="use-credentials"
                    className="w-full h-full object-cover"
                  />
                  {isLinked && (
                    <span
                      className="absolute top-1 right-1 px-1 py-0.5 rounded text-[9px] font-semibold backdrop-blur-md"
                      style={{ background: 'rgba(217,160,91,0.85)', color: '#1a1a1f' }}
                    >
                      LIÉ
                    </span>
                  )}
                  <span
                    className="absolute bottom-0 inset-x-0 px-1.5 py-1 text-[9.5px] truncate text-left"
                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)', color: '#fff' }}
                  >
                    {c.filename}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <p className="text-[10.5px] mt-2" style={{ color: 'var(--c-text-faint)' }}>
          Cliquez sur une tuile pour <strong>lier / délier</strong> la référence visuelle à
          ce matériau. Pensez ensuite à <strong>Enregistrer</strong> en bas du tiroir.
        </p>
      </Field>
    </section>
  );
};

export default MaterialEditDrawer;
